import { Helmet } from 'react-helmet-async'

export function Privacy() {
  return (
    <div className="py-12 px-4">
      <Helmet>
        <title>Privacy Policy | VaultLynk</title>
        <meta name="description" content="Learn how VaultLynk collects, uses, and protects your personal data and uploaded files." />
      </Helmet>
      <div className="max-w-3xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Privacy Policy</h1>

        <div className="prose prose-gray max-w-none">
          <p className="text-gray-600 mb-6">
            Last updated: February 6, 2026
          </p>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-600 mb-4">
              VaultLynk ("we", "our", or "us") respects your privacy and is committed to protecting your
              personal data. This Privacy Policy explains how we collect, use, and safeguard your information
              when you use our file sharing service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>

            <h3 className="text-lg font-medium text-gray-900 mb-2">Account Information</h3>
            <p className="text-gray-600 mb-4">
              When you create an account, we collect:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li>Email address</li>
              <li>Password (encrypted)</li>
              <li>Account preferences</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-900 mb-2">File Data</h3>
            <p className="text-gray-600 mb-4">
              When you upload files, we store:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li>The file content (encrypted at rest)</li>
              <li>File metadata (name, size, type)</li>
              <li>Upload timestamp and expiration date</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-900 mb-2">Usage Data</h3>
            <p className="text-gray-600 mb-4">
              We automatically collect:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li>IP addresses (for analytics and security)</li>
              <li>Browser type and version</li>
              <li>Download statistics (for premium users)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-600 mb-4">
              We use your information to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li>Provide and maintain our Service</li>
              <li>Process your uploads and downloads</li>
              <li>Manage your account and subscriptions</li>
              <li>Send important service notifications</li>
              <li>Improve and optimize our Service</li>
              <li>Detect and prevent fraud or abuse</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">4. Data Storage and Security</h2>
            <p className="text-gray-600 mb-4">
              Your files are stored securely using Cloudflare R2 with encryption at rest.
              All data transfers occur over HTTPS. We implement industry-standard security
              measures to protect your data.
            </p>
            <p className="text-gray-600 mb-4">
              Files are automatically deleted after their expiration period. We do not access
              or view your file contents unless required by law or to investigate abuse.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Data Sharing</h2>
            <p className="text-gray-600 mb-4">
              We do not sell your personal data. We may share information with:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li><strong>Service providers:</strong> Stripe (payments), Cloudflare (storage), Google (authentication)</li>
              <li><strong>Legal authorities:</strong> When required by law or to protect our rights</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Your Rights</h2>
            <p className="text-gray-600 mb-4">
              You have the right to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li>Access your personal data</li>
              <li>Correct inaccurate data</li>
              <li>Delete your account and associated data</li>
              <li>Export your data</li>
              <li>Opt out of marketing communications</li>
            </ul>
            <p className="text-gray-600 mb-4">
              To exercise these rights, contact us at support@vaultlynk.com.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Cookies</h2>
            <p className="text-gray-600 mb-4">
              We use essential cookies to maintain your session and preferences. We do not use
              tracking cookies for advertising purposes.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Data Retention</h2>
            <p className="text-gray-600 mb-4">
              We retain your account data for as long as your account is active. Files are deleted
              according to their expiration settings. After account deletion, we remove your personal
              data within 30 days, except where retention is required by law.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Changes to This Policy</h2>
            <p className="text-gray-600 mb-4">
              We may update this Privacy Policy from time to time. We will notify you of significant
              changes via email or through the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">10. Contact Us</h2>
            <p className="text-gray-600 mb-4">
              For privacy-related questions or concerns, contact us at:
            </p>
            <p className="text-gray-600">
              Email: support@vaultlynk.com
            </p>
          </section>
        </div>
      </div>
    </div>
  )
}
